import React from "react";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import PageContainer from "../../components/common/PageContainer";
import { graphql, useLazyLoadQuery } from "react-relay";
import TabPanel from "../../components/common/TabPanel";
import { Box, Tab, Tabs, Typography, styled } from "@mui/material";
import { LogicTreePageQuery } from "./__generated__/LogicTreePageQuery.graphql";
import { SourceBranchesTab } from "./SourceBranchesTab";
import TabContainer from "../../components/common/TabContainer";
import { useNavigate, useParams } from "react-router-dom";
import { GroundMotionBranchesTab } from "./GroundMotionBranchesTab";
import SelectControl from "../../components/common/SelectControl";
import CentredCircularProgress from "../../components/common/CentredCircularProgress";

export type LogicTreeViewerParams = {
  tab: string;
};

const StyledTab = styled(Tab)(({ theme }) => ({
  maxWidth: "5vw",
  borderRight: `2px solid ${theme.palette.divider}`,
}));

export const LogicTreeViewer = () => {
  const [logicTreeVersion, setLogicTreeVersion] = React.useState<string>("");
  const data = useLazyLoadQuery<LogicTreePageQuery>(logicTreePageQuery, { version: logicTreeVersion });
  const { tab } = useParams<LogicTreeViewerParams>();
  const logicTreeVersions = data?.get_models?.map((model) => model?.version || "") ?? [];
  const navigate = useNavigate();

  const renderTab = () => {
    switch (tab) {
      default:
        return <TabPanel>{data && <SourceBranchesTab data={data} />}</TabPanel>;
      case "GroundMotionBranches":
        return <TabPanel>{data && <GroundMotionBranchesTab data={data} />}</TabPanel>;
    }
  };

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5">Logic Tree</Typography>
      <Box sx={{ marginTop: "10px" }}>
        <SelectControl
          name="Model Version"
          selection={logicTreeVersion}
          setSelection={setLogicTreeVersion}
          options={logicTreeVersions || []}
        />
      </Box>

      <TabContainer maxWidth="xl">
        <Tabs
          orientation="vertical"
          value={tab ?? "SourceBranches"}
          onChange={(_e, val) => {
            navigate(`/LogicTree/${val}`);
          }}
        >
          <StyledTab label="Source Branches" id="SourceBranches" value={"SourceBranches"} />
          <StyledTab label="Ground Motion Branches" id="GroundMotionBranches" value={"GroundMotionBranches"} />
        </Tabs>
        {renderTab()}
      </TabContainer>
    </PageContainer>
  );
};

export const LogicTreePage = () => {
  return (
    <ErrorBoundary>
      <React.Suspense
        fallback={
          <PageContainer>
            <CentredCircularProgress />
          </PageContainer>
        }
      >
        <LogicTreeViewer />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default LogicTreePage;

const logicTreePageQuery = graphql`
  query LogicTreePageQuery($version: String!) {
    get_model(version: $version) {
      id
      version
      title
      gmm_logic_tree {
        id
        branch_sets {
          model_version
          short_name
          long_name
          tectonic_region_type
          branches {
            gsim_name
            gsim_args
            weight
          }
        }
      }
      source_logic_tree {
        id
        branch_sets {
          id
          short_name
          long_name
          branches {
            tag
            weight
            sources {
              __typename
              ... on BranchInversionSource {
                nrml_id
                inversion_id
                rupture_set_id
              }
              ... on BranchDistributedSource {
                nrml_id
              }
            }
          }
        }
      }
    }
    current_model_version
    get_models {
      id
      version
      title
    }
  }
`;
